import React from 'react';
import { parseSelectionsFromCurrentValue, getDropdownDisplayText } from '../helpers/chartHelpers';

interface ActionDropdownProps {
  index: number;
  image?: string;
  text?: string;
  style?: React.CSSProperties;
  action?: () => void;
  isDropdown: boolean;
  dropdownOptions: { value: string; label: string }[];
  currentValue?: string;
  onSelect?: (value: string) => void;
  openDropdownIndex: number | null;
  onDropdownToggle: (index: number) => void;
  dropdownSelections: { [key: number]: Set<string> };
  setDropdownSelections: React.Dispatch<React.SetStateAction<{ [key: number]: Set<string> }>>;
  initialDropdownSelections: { [key: number]: Set<string> };
  setInitialDropdownSelections: React.Dispatch<React.SetStateAction<{ [key: number]: Set<string> }>>;
  lastCurrentValueRef: React.MutableRefObject<{ [key: number]: string | undefined }>;
  dropdownRefs: React.MutableRefObject<{ [key: number]: HTMLDivElement | null }>;
}

const ActionDropdown: React.FC<ActionDropdownProps> = ({
  index: idx,
  style,
  isDropdown,
  dropdownOptions,
  currentValue,
  onSelect,
  openDropdownIndex,
  onDropdownToggle,
  dropdownSelections,
  setDropdownSelections,
  initialDropdownSelections,
  setInitialDropdownSelections,
  lastCurrentValueRef,
  dropdownRefs,
}) => {
  // Ensure "all" option exists, add it if it doesn't
  const allOptionValue = 'all';
  const hasAllOption = dropdownOptions.some(opt => opt.value === allOptionValue);
  const processedOptions = hasAllOption 
    ? dropdownOptions 
    : [{ value: allOptionValue, label: 'All' }, ...dropdownOptions];
  
  const otherOptions = processedOptions.filter(opt => opt.value !== allOptionValue);
  
  // Get current selections - robust initialization and syncing
  const getCurrentSelections = (): Set<string> => {
    const isDropdownOpen = openDropdownIndex === idx;
    const lastKnownValue = lastCurrentValueRef.current[idx];
    const currentValueChanged = lastKnownValue !== currentValue;
    
    // If dropdown is open, always use stored selections (preserve user interactions)
    if (isDropdownOpen && dropdownSelections[idx]) {
      return dropdownSelections[idx];
    }
    
    // If we have stored selections and currentValue hasn't changed externally, use stored
    if (dropdownSelections[idx] && !currentValueChanged) {
      return dropdownSelections[idx];
    }
    
    // Parse from currentValue (either initial render or external change when closed)
    const parsedSelections = parseSelectionsFromCurrentValue(
      currentValue,
      processedOptions,
      allOptionValue,
      otherOptions
    );
    
    // Initialize or update dropdownSelections if needed
    if (!dropdownSelections[idx] || currentValueChanged) {
      // Only update if dropdown is closed to avoid interfering with user interactions
      if (!isDropdownOpen) {
        setDropdownSelections(prev => ({
          ...prev,
          [idx]: parsedSelections
        }));
        lastCurrentValueRef.current[idx] = currentValue;
      }
    }
    
    return parsedSelections;
  };
  
  const selectedValues = getCurrentSelections();
  
  // Check if all non-"all" options are selected
  const allOtherOptionsSelected = otherOptions.length > 0 && 
    otherOptions.every(opt => selectedValues.has(opt.value));
  
  // If "all" is selected, all other options should be selected
  const isAllSelected = selectedValues.has(allOptionValue);
  
  const handleDropdownToggle = () => {
    const wasOpen = openDropdownIndex === idx;
    if (wasOpen) {
      // Closing dropdown - only call onSelect if selections changed
      const initialSelections = initialDropdownSelections[idx];
      if (onSelect && initialSelections) {
        // Compare current selections with initial selections
        const currentValues = Array.from(selectedValues).filter(v => v !== allOptionValue).sort();
        const initialValues = Array.from(initialSelections).filter(v => v !== allOptionValue).sort();
        
        // Check if selections have changed
        const hasChanged = currentValues.length !== initialValues.length ||
          !currentValues.every((val, i) => val === initialValues[i]);
        
        if (hasChanged) {
          // Pass comma-separated string of selected values
          onSelect(currentValues.join(','));
        }
      }
      // Clear initial selections and update last known value
      setInitialDropdownSelections(prev => {
        const updated = { ...prev };
        delete updated[idx];
        return updated;
      });
      // Update last known value to reflect the final state
      if (onSelect) {
        const finalValues = Array.from(selectedValues).filter(v => v !== allOptionValue).join(',');
        lastCurrentValueRef.current[idx] = finalValues || undefined;
      }
    } else {
      // Opening dropdown - sync with currentValue if it changed externally
      const lastKnownValue = lastCurrentValueRef.current[idx];
      const currentValueChanged = lastKnownValue !== currentValue;
      
      let selectionsToUse = selectedValues;
      
      // If currentValue changed externally, sync selections
      if (currentValueChanged && currentValue !== undefined) {
        const syncedSelections = parseSelectionsFromCurrentValue(
          currentValue,
          processedOptions,
          allOptionValue,
          otherOptions
        );
        selectionsToUse = syncedSelections;
        setDropdownSelections(prev => ({
          ...prev,
          [idx]: syncedSelections
        }));
        lastCurrentValueRef.current[idx] = currentValue;
      } else if (!dropdownSelections[idx]) {
        // Initialize if doesn't exist
        setDropdownSelections(prev => ({
          ...prev,
          [idx]: new Set(selectedValues)
        }));
        lastCurrentValueRef.current[idx] = currentValue;
      }
      
      // Store initial selections for change detection
      setInitialDropdownSelections(prev => ({
        ...prev,
        [idx]: new Set(selectionsToUse)
      }));
    }
    onDropdownToggle(idx);
  };
  
  const handleDropdownClose = () => {
    if (openDropdownIndex === idx) {
      // Closing dropdown - only call onSelect if selections changed
      const initialSelections = initialDropdownSelections[idx];
      if (onSelect && initialSelections) {
        // Compare current selections with initial selections
        const currentValues = Array.from(selectedValues).filter(v => v !== allOptionValue).sort();
        const initialValues = Array.from(initialSelections).filter(v => v !== allOptionValue).sort();
        
        // Check if selections have changed
        const hasChanged = currentValues.length !== initialValues.length ||
          !currentValues.every((val, i) => val === initialValues[i]);
        
        if (hasChanged) {
          // Pass comma-separated string of selected values
          const finalValues = currentValues.join(',');
          onSelect(finalValues);
          // Update last known value to reflect the final state
          lastCurrentValueRef.current[idx] = finalValues || undefined;
        }
      }
      // Clear initial selections
      setInitialDropdownSelections(prev => {
        const updated = { ...prev };
        delete updated[idx];
        return updated;
      });
      onDropdownToggle(idx); // Toggle to close (will be handled by parent)
    }
  };
  
  const handleOptionToggle = (optionValue: string) => {
    const newSelections = new Set(selectedValues);
    
    if (optionValue === allOptionValue) {
      // Toggle "all" option
      if (newSelections.has(allOptionValue)) {
        // Deselect "all" - deselect all other options
        newSelections.delete(allOptionValue);
        otherOptions.forEach(opt => newSelections.delete(opt.value));
      } else {
        // Select "all" - select all other options
        newSelections.add(allOptionValue);
        otherOptions.forEach(opt => newSelections.add(opt.value));
      }
    } else {
      // Toggle individual option
      if (newSelections.has(optionValue)) {
        newSelections.delete(optionValue);
        // If deselecting an option, also deselect "all"
        newSelections.delete(allOptionValue);
      } else {
        newSelections.add(optionValue);
        // If all other options are now selected, also select "all"
        const allSelected = otherOptions.every(opt => 
          optionValue === opt.value || newSelections.has(opt.value)
        );
        if (allSelected) {
          newSelections.add(allOptionValue);
        }
      }
    }
    
    setDropdownSelections(prev => ({ ...prev, [idx]: newSelections }));
  };
  
  const displayText = getDropdownDisplayText(selectedValues, allOptionValue, otherOptions);
  
  return (
    <div
      ref={(el) => { dropdownRefs.current[idx] = el; }}
      style={{ position: 'relative', display: 'inline-block' }}
    >
      <button
        className="action-button"
        onClick={handleDropdownToggle}
        style={{
          position: 'relative',
          paddingRight: '30px',
          paddingLeft: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          backgroundColor: 'white',
          color: '#333',
          minWidth: '80px',
          ...style,
        }}
      >
        {displayText}
        <span style={{
          display: 'inline-block',
          width: '12px',
          height: '12px',
          backgroundImage: `url('${__dirname}dropdown.png')`,
          backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          position: 'absolute',
          right: '10px',
        }} />
      </button>
      {openDropdownIndex === idx && (
        <div 
          onMouseLeave={handleDropdownClose}
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: '5px',
            backgroundColor: 'white',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            zIndex: 1000,
            width: '100%',
            overflow: 'hidden',
            minWidth: '120px',
          }}>
          <button
            onClick={() => handleOptionToggle(allOptionValue)}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: 'none',
              borderBottom: '1px solid rgba(0, 0, 0, 0.04)',
              backgroundColor: isAllSelected ? 'rgba(0, 122, 255, 0.08)' : 'transparent',
              textAlign: 'left',
              cursor: 'pointer',
              fontSize: '12px',
              color: isAllSelected ? '#007AFF' : '#1d1d1f',
              fontWeight: isAllSelected ? '500' : '400',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(0, 122, 255, 0.04)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = isAllSelected ? 'rgba(0, 122, 255, 0.08)' : 'transparent';
            }}
          >
            <span style={{
              width: '14px',
              height: '14px',
              border: '1.5px solid #007AFF',
              borderRadius: '3px',
              backgroundColor: isAllSelected ? '#007AFF' : 'transparent',
              boxShadow: isAllSelected ? '0 2px 8px rgba(0, 122, 255, 0.3)' : 'none',
              position: 'relative',
              flexShrink: 0,
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              {isAllSelected && (
                <span style={{
                  color: 'white',
                  fontSize: '9px',
                  fontWeight: 'bold',
                  lineHeight: 1,
                }}>✓</span>
              )}
            </span>
            All
          </button>
          {otherOptions.map((option: { value: string; label: string }) => {
            const isSelected = selectedValues.has(option.value);
            return (
              <button
                key={option.value}
                onClick={() => handleOptionToggle(option.value)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: 'none',
                  borderBottom: '1px solid rgba(0, 0, 0, 0.04)',
                  backgroundColor: isSelected ? 'rgba(0, 122, 255, 0.08)' : 'transparent',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontSize: '12px',
                  color: isSelected ? '#007AFF' : '#1d1d1f',
                  fontWeight: isSelected ? '500' : '400',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  transition: 'all 0.15s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(0, 122, 255, 0.04)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = isSelected ? 'rgba(0, 122, 255, 0.08)' : 'transparent';
                }}
              >
                <span style={{
                  width: '14px',
                  height: '14px',
                  border: '1.5px solid #007AFF',
                  borderRadius: '3px',
                  backgroundColor: isSelected ? '#007AFF' : 'transparent',
                  boxShadow: isSelected ? '0 2px 8px rgba(0, 122, 255, 0.3)' : 'none',
                  position: 'relative',
                  flexShrink: 0,
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  {isSelected && (
                    <span style={{
                      color: 'white',
                      fontSize: '9px',
                      fontWeight: 'bold',
                      lineHeight: 1,
                    }}>✓</span>
                  )}
                </span>
                {option.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ActionDropdown;
